/**
 * The "New layout (beta)" setting as a hook: whether this person sees the new shell, and the switch.
 * Read by App (which layout to draw), Settings (the switch) and the profile menu (the staff switch).
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useStaffState } from '@/cowork/useIsStaff';
import { LAYOUT_EVENT, maySwitchLayout, newLayoutOn, setNewLayout } from './layoutPref';

export function useNewLayout() {
  const { user } = useAuth();
  const { staff } = useStaffState();
  const location = useLocation();
  // While /me loads, the last answer for this login holds (staff on staging must not see the classic layout flash).
  const knownKey = `omni.staff:${(user?.email || '').toLowerCase()}`;
  let known = false;
  try { known = localStorage.getItem(knownKey) === '1'; } catch { /* storage off */ }
  useEffect(() => { if (staff !== null && user?.email) { try { localStorage.setItem(knownKey, staff ? '1' : '0'); } catch { /* storage off */ } } }, [staff, knownKey, user?.email]);
  const isStaff = staff === null ? known : staff;
  const viewer = { email: user?.email ?? null, staff: isStaff, environment: import.meta.env.VITE_ENVIRONMENT as string | undefined };
  const [, bump] = useState(0);
  useEffect(() => {
    const on = () => bump((n) => n + 1);
    window.addEventListener(LAYOUT_EVENT, on);
    window.addEventListener('storage', on);
    return () => { window.removeEventListener(LAYOUT_EVENT, on); window.removeEventListener('storage', on); };
  }, []);
  const email = viewer.email;
  const set = useCallback((value: boolean) => setNewLayout(email, value), [email]);
  // A link with ?layout=new or ?layout=classic sets the choice (it holds after the next page change).
  const asked = new URLSearchParams(location.search).get('layout');
  useEffect(() => { if (user && (asked === 'new' || asked === 'classic')) setNewLayout(email, asked === 'new'); }, [asked, email, user]);
  return {
    on: !!user && newLayoutOn(viewer, location.search),
    set,
    mayToggle: !!user && maySwitchLayout(viewer),
    staff: isStaff,
  };
}
